import { useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import type { Product, ProductQuota } from '../lib/types'
import {
  agentsPurchaseForecast,
  agentsTrialPurchaseForecast,
  mcpPurchaseForecast,
} from '../lib/forecast'
import { SubHead } from './ui/SubHead'
import { SimulationBadge } from './ui/SimulationBadge'
import { ProjectedControl } from './AgentsCreditsBalanceCard'
import { VolumeChart, type ChartSeries } from './VolumeChart'

/** The Additional Volume purchase simulation (MS-820; prototype `volSimBlock`
 * + `volForecast` + `volSeries`): a dashed teal `.sim-box` with the Simulation
 * badge top-right, the "Balance over the next 12 months" heading and its note,
 * the chart, and the projected-consumption control in the footer.
 *
 * - Mambu MCP: monthly available volume — Consumed / Contracted / On Demand,
 *   with the cart as "This Purchase" for its 12 months (never past the
 *   contract end, Q2) and the projected consumption as a dashed reference
 *   line the bars are read against. On trial: Consumed / Trial Volume / This
 *   Purchase.
 * - Mambu Agents: the depleting balance — Balance Today / Projected Balance
 *   at Start of Month / This Purchase, the first month prorated (Q20). On
 *   trial (Q21, no prototype reference — ADR deviation): Trial Volume / This
 *   Purchase.
 *
 * Shown on Trial too (Q14): only Purchase is locked there. */
export function PurchaseForecastChart({
  product,
  quota,
  trial,
  cartVolume,
  burn,
  onBurnChange,
  contractStart,
  contractEnd,
}: Readonly<{
  product: Product
  quota: ProductQuota
  trial: boolean
  cartVolume: number
  burn: number
  onBurnChange: (next: number) => void
  contractStart: string | null
  contractEnd: string | null
}>) {
  const { t } = useTranslation()
  const isAgents = product === 'Mambu Agents'

  const months = useMemo(() => {
    const today = new Date()
    if (!isAgents) return mcpPurchaseForecast(quota, today, cartVolume, contractEnd, trial)
    if (trial) return agentsTrialPurchaseForecast(quota, burn, cartVolume, today)
    return agentsPurchaseForecast(
      quota.balanceRemaining ?? 0,
      quota.includedVolume,
      contractStart,
      contractEnd,
      burn,
      cartVolume,
      today
    )
  }, [isAgents, quota, cartVolume, contractEnd, contractStart, trial, burn])

  const thisPurchase: ChartSeries = { key: 'sim', label: t('additionalVolume.sim.thisPurchase'), tone: 'simGhost' }
  const trialVolume: ChartSeries = { key: 'trial', label: t('subscription.balance.trialVolume'), tone: 'contracted' }
  let series: ChartSeries[]
  if (isAgents) {
    series = trial
      ? [trialVolume, thisPurchase]
      : [
          { key: 'starting', label: t('subscription.creditsBalance.balanceToday'), tone: 'starting' },
          { key: 'base', label: t('subscription.creditsBalance.projectedStart'), tone: 'projected' },
          thisPurchase,
        ]
  } else {
    const consumed: ChartSeries = { key: 'consumed', label: t('subscription.balance.consumed'), tone: 'consumed' }
    series = trial
      ? [consumed, trialVolume, thisPurchase]
      : [
          consumed,
          { key: 'contracted', label: t('subscription.balance.contracted'), tone: 'contracted' },
          { key: 'onDemand', label: t('subscription.balance.onDemand'), tone: 'onDemand' },
          thisPurchase,
        ]
  }
  const projectedLabel = t(isAgents ? 'additionalVolume.sim.projectedAgt' : 'additionalVolume.sim.projectedMcp')

  return (
    <div className="relative border border-dashed border-[var(--sim-frame)] rounded-[10px] p-4" data-testid="sim-box">
      <SimulationBadge className="absolute top-4 right-4" />
      <div className="flex items-center gap-2.5 mb-3.5 flex-wrap pr-7">
        <SubHead
          label={t('additionalVolume.sim.heading')}
          info={t(isAgents ? 'additionalVolume.sim.infoAgt' : 'additionalVolume.sim.infoMcp')}
          infoWidth={340}
          className=""
        />
      </div>
      <VolumeChart
        months={months}
        series={series}
        legendTitle={t(isAgents ? 'subscription.creditsBalance.legendTitle' : 'subscription.balance.legendTitle')}
        yTitle={t(isAgents ? 'subscription.creditsBalance.axisTitle' : 'subscription.balance.axisTitle')}
        totalLabel={() =>
          t(isAgents ? 'subscription.creditsBalance.total' : 'subscription.balance.monthlyVolume')
        }
        hline={isAgents ? undefined : { value: burn, label: projectedLabel }}
      />
      <div className="flex justify-end mt-3">
        <ProjectedControl label={projectedLabel} value={burn} onChange={onBurnChange} />
      </div>
    </div>
  )
}
